
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { InventoryMovement, Warehouse } from "@/api/entities";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Search, Calendar } from "lucide-react";
import { format } from "date-fns";

export default function StockView() {
  const { t } = useTranslation();
  const [stockData, setStockData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [warehouses, setWarehouses] = useState([]);

  // Load warehouses separately
  const loadWarehouses = async () => {
    try {
      const warehousesData = await Warehouse.list({ is_active: true });
      setWarehouses(warehousesData || []);
    } catch (error) {
      console.error("Error loading warehouses:", error);
      setWarehouses([]);
    }
  };

  // Load stock data from server
  const loadStockData = async () => {
    setIsLoading(true);
    try {
      const warehouseId = warehouseFilter === 'all' ? null : warehouseFilter;
      const response = await InventoryMovement.getStockBalances(selectedDate, warehouseId);
      setStockData(response.stock || []);
    } catch (error) {
      console.error("Error loading stock data:", error);
      setStockData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load warehouses when component mounts
  useEffect(() => {
    loadWarehouses();
  }, []);

  // Load data when component mounts or filters change
  useEffect(() => {
    loadStockData();
  }, [selectedDate, warehouseFilter]);

  // Filter stock data based on search term
  const filteredStock = useMemo(() => {
    if (!searchTerm) return stockData;
    
    return stockData.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (item.item_name && item.item_name.toLowerCase().includes(searchLower)) ||
        (item.item_type && item.item_type.toLowerCase().includes(searchLower)) ||
        (item.item_sku && item.item_sku.toLowerCase().includes(searchLower)) ||
        (item.warehouse_name && item.warehouse_name.toLowerCase().includes(searchLower))
      );
    });
  }, [stockData, searchTerm]);

  // Handlers
  const handleDateChange = useCallback((e) => {
    setSelectedDate(e.target.value);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleWarehouseChange = useCallback((value) => {
    setWarehouseFilter(value);
  }, []);

  if (isLoading) {
    return <div className="text-center py-12 text-slate-500 elegant-card">
      {t('inventory.stock.loading')}
    </div>;
  }
  
  return (
    <div className="space-y-6">
      <Card className="bg-gray-50 rounded-lg border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-gray-900 font-bold">
            <Calendar className="w-5 h-5 text-orange-500" />
            {t('inventory.stock.balances_on_date')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Date selector */}
            <div className="flex-1">
              <Input
                id="stock-date"
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="bg-white border-gray-200 focus:border-orange-500 focus:ring-orange-500"
                aria-label="Date for stock calculation"
              />
            </div>
            
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder={t('inventory.stock.search_placeholder')}
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 bg-white border-gray-200 focus:border-orange-500 focus:ring-orange-500"
              />
            </div>
            
            {/* Warehouse filter */}
            <div className="flex-1">
              <Select value={warehouseFilter} onValueChange={handleWarehouseChange}>
                <SelectTrigger className="w-full bg-white border-gray-200 focus:border-orange-500 focus:ring-orange-500">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('inventory.stock.all_warehouses')}</SelectItem>
                  {warehouses.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Info block */}
          <div className="bg-gray-100 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-orange-600" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {t('inventory.stock.balances_shown_for')} {format(new Date(selectedDate), 'dd.MM.yyyy')}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {t('inventory.stock.balances_description')}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="font-bold text-slate-900">{t('inventory.stock.item')}</TableHead>
                <TableHead className="font-bold text-slate-900">{t('inventory.stock.type')}</TableHead>
                <TableHead className="font-bold text-slate-900">SKU</TableHead>
                <TableHead className="font-bold text-slate-900">{t('inventory.stock.warehouse')}</TableHead>
                <TableHead className="text-right font-bold text-slate-900">{t('inventory.stock.balance')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStock.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <Package className="w-12 h-12 text-slate-300" />
                      <p className="font-medium">{t('inventory.stock.no_balance_data')}</p>
                      <p className="text-sm">{t('inventory.stock.no_balance_for_date')} ({format(new Date(selectedDate), 'dd.MM.yyyy')})</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredStock.map(item => (
                  <TableRow key={item.id} className="hover:bg-slate-50/50">
                    <TableCell className="font-medium text-slate-900">{item.item_name}</TableCell>
                    <TableCell className="text-slate-600">{item.item_type}</TableCell>
                    <TableCell className="font-mono text-sm text-slate-500">{item.item_sku}</TableCell>
                    <TableCell className="text-slate-600">{item.warehouse_name}</TableCell>
                    <TableCell className="text-right font-bold text-lg text-slate-900">{item.quantity}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
